/**
 * Isolated component renderer — no sidebar, no controls.
 *
 * Route: /workshop/export/:componentId
 *
 * Point a Figma DOM plugin (e.g. html.to.design) at this URL to import
 * a single component cleanly. The component renders centred on a canvas
 * background with native-glass tokens active.
 */

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LayoutProvider } from "@/components/layout/layout-provider";
import { CHROME_GROUPS } from "./export-chrome";
import { FRAME_GROUP } from "./export-frames";
import { APP_GROUP } from "./export-app";
import type { ExportEntry, ExportGroup } from "./export-types";

const GROUPS: ExportGroup[] = [APP_GROUP, ...CHROME_GROUPS, FRAME_GROUP];
const ALL_ENTRIES: ExportEntry[] = GROUPS.flatMap((g) => g.entries);

export function ExportIsolatedPage() {
  const { componentId } = useParams<{ componentId: string }>();
  const entry = ALL_ENTRIES.find((e) => e.id === componentId);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.desktopDenk = "true";
    root.dataset.desktopGlass = "true";
    root.dataset.desktopNativeGlass = "true";
    return () => {
      delete root.dataset.desktopDenk;
      delete root.dataset.desktopGlass;
      delete root.dataset.desktopNativeGlass;
    };
  }, []);

  if (!entry) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0A0A0B] text-white/40 text-sm">
        Component not found: {componentId}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-canvas p-12"
      data-testid="export-isolated-stage"
    >
      <LayoutProvider>{entry.render()}</LayoutProvider>
    </div>
  );
}
